import { InstagramLogo, FacebookLogo, TiktokLogo, GoogleLogo, AppleLogo } from '@phosphor-icons/react';

/**
 * 5 social-login buttons in brand colors, ordered:
 *   Instagram (gradient purple→pink→orange)
 *   Facebook  (blue #1877F2)
 *   TikTok    (white/black with cyan+pink shadow)
 *   Google    (multicolor — uses Google G mark)
 *   Apple     (white on black)
 *
 * Google is real (Emergent OAuth via `onGoogle`); the rest are mocked via `onMock(name)`.
 */
export default function SocialAuthRow({ onGoogle, onMock }) {
  const handler = (name) => () => onMock?.(name);

  const baseBtn =
    'h-12 w-full rounded-2xl flex items-center justify-center transition-transform active:scale-[0.95] relative overflow-hidden';

  return (
    <div className="grid grid-cols-5 gap-2.5" data-testid="social-auth-row">
      {/* Instagram — gradient pill */}
      <button
        type="button"
        onClick={handler('Instagram')}
        className={baseBtn}
        style={{
          background:
            'linear-gradient(45deg, #515BD4 0%, #8134AF 25%, #DD2A7B 55%, #FEDA77 90%, #F58529 100%)',
          boxShadow: '0 6px 18px -8px rgba(221,42,123,0.55)',
        }}
        data-testid="login-instagram"
        aria-label="Continue with Instagram"
      >
        <InstagramLogo size={22} weight="bold" color="#ffffff" />
      </button>

      {/* Facebook — solid blue */}
      <button
        type="button"
        onClick={handler('Facebook')}
        className={baseBtn}
        style={{
          backgroundColor: '#1877F2',
          boxShadow: '0 6px 18px -8px rgba(24,119,242,0.55)',
        }}
        data-testid="login-facebook"
        aria-label="Continue with Facebook"
      >
        <FacebookLogo size={22} weight="fill" color="#ffffff" />
      </button>

      {/* TikTok — black with TikTok cyan+pink shadow */}
      <button
        type="button"
        onClick={handler('TikTok')}
        className={baseBtn}
        style={{
          backgroundColor: '#000000',
          boxShadow:
            '-2px 0 0 #25F4EE inset, 2px 0 0 #FE2C55 inset, 0 6px 18px -8px rgba(254,44,85,0.45)',
        }}
        data-testid="login-tiktok"
        aria-label="Continue with TikTok"
      >
        <TiktokLogo size={22} weight="bold" color="#ffffff" />
      </button>

      {/* Google — white with multicolor G */}
      <button
        type="button"
        onClick={onGoogle}
        className={baseBtn}
        style={{
          backgroundColor: '#ffffff',
          boxShadow: '0 6px 18px -8px rgba(255,255,255,0.4)',
        }}
        data-testid="login-google"
        aria-label="Continue with Google"
      >
        <GoogleLogo size={22} weight="bold" color="#4285F4" />
      </button>

      {/* Apple — pure white with apple logo */}
      <button
        type="button"
        onClick={handler('Apple')}
        className={baseBtn}
        style={{
          backgroundColor: '#ffffff',
          boxShadow: '0 6px 18px -8px rgba(255,255,255,0.4)',
        }}
        data-testid="login-apple"
        aria-label="Continue with Apple"
      >
        <AppleLogo size={22} weight="fill" color="#000000" />
      </button>
    </div>
  );
}
